import React from "react";
import {
  Button,
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import type {
  ClientJobRole,
  ClientUser,
  EnabledServices,
  ServiceOverrides,
} from "../api/clientManagement";

type ServiceKey = keyof EnabledServices;

interface UserRowProps {
  user: ClientUser;
  departments: { id: string; name: string }[];
  jobRoles: ClientJobRole[];
  departmentSelection: string;
  jobRoleSelection: string;
  serviceOverrides: ServiceOverrides;
  enabledServices: EnabledServices;
  statusLabel: string;
  statusTone: "success" | "muted";
  canEditServices: boolean;
  isSavingServices: boolean;
  onRemoveFromClient: () => void;
  onRemoveFromDepartment: (departmentId: string) => void;
  onRemoveFromJobRole: (jobRoleId: string) => void;
  onDepartmentChange: (value: string) => void;
  onJobRoleChange: (value: string) => void;
  onAssignDepartment: (departmentId: string) => void;
  onAssignJobRole: (jobRoleId: string) => void;
  onServiceOverrideChange: (serviceKey: ServiceKey, value: boolean | null) => void;
  onSaveServiceOverrides: () => void;
}

export const UserRow: React.FC<UserRowProps> = ({
  user,
  departments,
  jobRoles,
  departmentSelection,
  jobRoleSelection,
  serviceOverrides,
  enabledServices,
  statusLabel,
  statusTone,
  canEditServices,
  isSavingServices,
  onRemoveFromClient,
  onRemoveFromDepartment,
  onRemoveFromJobRole,
  onDepartmentChange,
  onJobRoleChange,
  onAssignDepartment,
  onAssignJobRole,
  onServiceOverrideChange,
  onSaveServiceOverrides,
}) => (
  <TableRow>
    <TableCell>
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={600}>
          {user.first_name} {user.last_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user.email}
        </Typography>
        {user.departments.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Dept: {user.departments.map((dept) => dept.name).join(", ")}
          </Typography>
        )}
        {user.job_roles.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Role: {user.job_roles.map((role) => role.name).join(", ")}
          </Typography>
        )}
        {!!user.integrations?.length && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {user.integrations.map((integration) => (
              <Box
                key={`${integration.provider}-${integration.name}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: integration.connected
                    ? "rgba(46, 125, 50, 0.35)"
                    : "divider",
                  bgcolor: integration.connected
                    ? "rgba(46, 125, 50, 0.08)"
                    : "background.paper",
                }}
              >
                {integration.icon ? (
                  <Box
                    component="img"
                    src={integration.icon}
                    alt={integration.name}
                    sx={{ width: 14, height: 14 }}
                  />
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  {integration.name}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </TableCell>
    <TableCell>
      <Stack spacing={1}>
        <FormControl size="small" fullWidth>
          <Select
            value={departmentSelection}
            displayEmpty
            onChange={(event) => onDepartmentChange(event.target.value)}
            renderValue={(value) => {
              if (!value) return "Select department";
              return departments.find((dept) => dept.id === value)?.name ?? value;
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          disabled={!departmentSelection}
          onClick={() => onAssignDepartment(departmentSelection)}
        >
          Assign
        </Button>
        {user.departments.map((dept) => (
          <Button
            key={dept.id}
            variant="text"
            size="small"
            onClick={() => onRemoveFromDepartment(dept.id)}
          >
            Remove {dept.name}
          </Button>
        ))}
      </Stack>
    </TableCell>
    <TableCell>
      <Stack spacing={1}>
        <FormControl size="small" fullWidth>
          <Select
            value={jobRoleSelection}
            displayEmpty
            onChange={(event) => onJobRoleChange(event.target.value)}
            renderValue={(value) => {
              if (!value) return "Select job role";
              return jobRoles.find((role) => role.id === value)?.name ?? value;
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {jobRoles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          disabled={!jobRoleSelection}
          onClick={() => onAssignJobRole(jobRoleSelection)}
        >
          Assign
        </Button>
        {user.job_roles.map((role) => (
          <Button
            key={role.id}
            variant="text"
            size="small"
            onClick={() => onRemoveFromJobRole(role.id)}
          >
            Remove {role.name}
          </Button>
        ))}
      </Stack>
    </TableCell>
    <TableCell>
      <Stack spacing={1}>
        {SERVICE_FIELDS.map((service) => {
          const value = toSelectValue(serviceOverrides[service.key]);
          const effectiveLabel = enabledServices[service.key]
            ? "Enabled"
            : "Disabled";
          return (
            <FormControl key={service.key} size="small" fullWidth>
              <Select
                value={value}
                onChange={(event) =>
                  onServiceOverrideChange(
                    service.key,
                    fromSelectValue(event.target.value)
                  )
                }
                disabled={!canEditServices || isSavingServices}
              >
                <MenuItem value="default">Use client default</MenuItem>
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
              <FormHelperText>
                {service.label}: {effectiveLabel}
              </FormHelperText>
            </FormControl>
          );
        })}
        {canEditServices ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onSaveServiceOverrides}
            disabled={isSavingServices}
          >
            Save services
          </Button>
        ) : null}
      </Stack>
    </TableCell>
    <TableCell>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1,
          py: 0.5,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          color: statusTone === "success" ? "success.dark" : "text.secondary",
          backgroundColor:
            statusTone === "success"
              ? "rgba(46, 125, 50, 0.12)"
              : "action.selected",
        }}
      >
        {statusLabel}
      </Box>
    </TableCell>
    <TableCell>
      <Button variant="text" size="small" onClick={onRemoveFromClient}>
        Remove from client
      </Button>
    </TableCell>
  </TableRow>
);

const SERVICE_FIELDS: { key: ServiceKey; label: string }[] = [
  { key: "learning_locker", label: "Learning Locker" },
  { key: "occupational_health", label: "Occupational Health" },
  { key: "wearables", label: "Wearables" },
  { key: "akea_life", label: "Akea Life" },
];

const toSelectValue = (value: boolean | null): "default" | "enabled" | "disabled" => {
  if (value === null) return "default";
  return value ? "enabled" : "disabled";
};

const fromSelectValue = (value: unknown): boolean | null => {
  if (value === "enabled") return true;
  if (value === "disabled") return false;
  return null;
};
